import {
  BaseModel,
  createProvidedModelContext,
  ReadonlyEventWatchable,
  ReadonlyWatchable,
  TargetableModel,
  Watchable,
  type DeveloperDetails,
} from '@cotera/client/v0/actions/framework';
import { BroadcastEventType, ResourceType } from '@cotera/api';
import type { BroadcastEventPayloadMap } from '@cotera/api';
import { PersistedWorkflow } from '@cotera/sdk/core';
import type {
  DatasetTriggerCriterion,
  DatasetTriggerRunMode,
} from '@cotera/sdk/core';
import type {
  TenantedClient,
  ApiErr,
} from '@cotera/client/app/etc/api/typed-client';
import { WatchablesFactory } from '../watchables/watchables-factory';
import type { UrlParamAdapter } from '@cotera/client/app/etc/url-param-watchable';
import { UrlParamWatchable } from '@cotera/client/app/etc/url-param-watchable';
import {
  EDITOR_TAB_QUERY_KEY,
  parseWorkflowEditorTabFromUrl,
  resolveWorkflowEditorTabSync,
  serializeWorkflowEditorTabToUrl,
} from '../etc/editor-tab-url';
import type { WorkflowGraphApiData } from './workflow-graph.types';
import {
  cloneWorkflowDraftState,
  createDraftStateFromGraph,
  createEmptyWorkflowDraftGraph,
  createWorkflowDraftState,
  DEFAULT_NEW_WORKFLOW_NAME,
  draftAddDatasetTrigger,
  draftBindExistingTrigger,
  draftPasteWorkflowNodes,
  draftPatchWebhookTriggerInstallState,
  isDraftOnlyTriggerInWorkflow,
  isWorkflowDraftDirty,
  isWorkflowDraftDirtyAgainstBaseline,
  type WorkflowDraftState,
  type WorkflowPasteAnchor,
} from './workflow-draft-graph';
import type { WorkflowClipboardPayload } from './workflow-node-clipboard';
import type { WorkflowTriggerOption } from '../trigger/hooks';
import { draftStateToGraphCreateInput } from './workflow-draft-to-graph-create-input';
import { WorkflowUndoHistory } from './workflow-undo-history';
import {
  agentTitleAliasesForGraph,
  healWorkflowGraphBindingSources,
} from './heal-workflow-binding-sources';
import {
  EMPTY_WORKFLOW_EDITOR_SELECTION,
  resolveConnectFromNodeId,
  type WorkflowEditorSelection,
  type WorkflowToolbarState,
} from './workflow-editor-toolbar-state';
import { WorkflowNodeModelRegistry } from './workflow-node-models';
import { WorkflowWebhookTriggerEditModel } from './workflow-node-models/workflow-webhook-trigger-edit.model';
import type {
  WorkflowRenderCatalog,
  WorkflowDisposable,
} from './workflow-render-catalog/types';
import { DEFAULT_WORKFLOW_RENDER_CATALOG } from './workflow-render-catalog/default-workflow-render-catalog';

const { PersistedWorkflowTriggerKind } = PersistedWorkflow;

export type {
  WorkflowEditorSelection,
  WorkflowToolbarState,
} from './workflow-editor-toolbar-state';

export type WorkflowEditorTab = 'graph' | 'runs';

/**
 * A brand-new dataset trigger to seed onto a new workflow, carried from the
 * "Add automation" control on a dataset. Everything the draft graph needs to
 * mint the trigger; the rest of a dataset trigger's shape takes its defaults.
 */
export type WorkflowSeedNewDatasetTrigger = {
  datasetId: string;
  name: string;
  criterion: DatasetTriggerCriterion;
  runMode: DatasetTriggerRunMode;
};

export type WorkflowResourceMeta = {
  folderId: string | null;
  folderScope?: 'user' | 'org' | 'external' | null;
  permission: 'view' | 'edit' | 'admin' | 'pending';
};

export type WorkflowApiData = {
  id: string;
  orgId: string;
  ownerId: string;
  name: string;
  status: 'active' | 'archived';
  workflowStatus?: string;
  workflowType?: string;
  idempotencyKey?: PersistedWorkflow.WorkflowIdempotencyKey | null;
  graph?: WorkflowGraphApiData;
  createdAt: Date;
  updatedAt: Date;
};

export type WorkflowResourceApiData = {
  workflow: WorkflowApiData;
  meta: WorkflowResourceMeta | null;
};

type WorkflowResourceState = {
  resource: WorkflowResourceApiData | null;
  status: 'loading' | 'ready' | 'not-found';
};

type WorkflowModelProps = {
  workflowId: string;
  orgId: string;
  userId: string;
  isNew: boolean;
  folderId?: string;
  /**
   * Existing trigger to pre-bind onto a brand-new workflow's graph, so
   * "Create new workflow" under a trigger opens attached to it rather than
   * blank. Ignored when `isNew` is false (server-hydrated graphs win).
   */
  seedTrigger?: WorkflowTriggerOption;
  /**
   * A brand-new dataset trigger to seed onto a new workflow's graph, so
   * "Add automation" on a dataset opens a draft already watching that dataset.
   *
   * Unlike {@link seedTrigger} this trigger does not exist server-side yet, so
   * it is seeded into the draft only — not the baseline. That is what makes the
   * save path *create* it rather than bind to a trigger id that isn't there, and
   * what surfaces the seeded workflow as the unsaved draft it is. Ignored when
   * `isNew` is false.
   */
  seedNewDatasetTrigger?: WorkflowSeedNewDatasetTrigger;
  client: Pick<TenantedClient, 'resource'>;
  watchablesFactory: Pick<
    WatchablesFactory,
    | 'makeResourceUpdatedWatchable'
    | 'makeWebhookTriggerInstallStateWatchable'
    | 'makeWebhookTriggerSettingsWatchable'
    | 'makeAgentNodeConfigWatchable'
    | 'releaseAgentNodeConfigWatchable'
  >;
  /** Canvas views + per-entity view models. Defaults to the Cotera workflow catalog. */
  renderCatalog?: WorkflowRenderCatalog;
  urlAdapter: UrlParamAdapter;
  /** Opens graph or runs when the URL has no `tab` query param. */
  initialEditorTab?: WorkflowEditorTab;
};

function toWorkflowApiData(raw: {
  id: string;
  orgId: string;
  ownerId: string;
  name: string;
  status: 'active' | 'archived';
  workflowStatus?: string;
  workflowType?: string;
  idempotencyKey?: PersistedWorkflow.WorkflowIdempotencyKey | null;
  graph?: WorkflowGraphApiData;
  createdAt: Date;
  updatedAt: Date;
}): WorkflowApiData {
  return {
    id: raw.id,
    orgId: raw.orgId,
    ownerId: raw.ownerId,
    name: raw.name,
    status: raw.status,
    workflowStatus: raw.workflowStatus,
    workflowType: raw.workflowType,
    idempotencyKey: raw.idempotencyKey ?? null,
    graph: raw.graph,
    createdAt:
      raw.createdAt instanceof Date ? raw.createdAt : new Date(raw.createdAt),
    updatedAt:
      raw.updatedAt instanceof Date ? raw.updatedAt : new Date(raw.updatedAt),
  };
}

function toWorkflowResourceApiData(
  workflow: {
    id: string;
    orgId: string;
    ownerId: string;
    name: string;
    status: 'active' | 'archived';
    workflowStatus?: string;
    workflowType?: string;
    idempotencyKey?: PersistedWorkflow.WorkflowIdempotencyKey | null;
    graph?: WorkflowGraphApiData;
    createdAt: Date;
    updatedAt: Date;
  },
  meta: WorkflowResourceMeta | null
): WorkflowResourceApiData {
  return {
    workflow: toWorkflowApiData(workflow),
    meta,
  };
}

function createInitialWorkflowResource(
  orgId: string,
  userId: string,
  workflowId: string
): WorkflowResourceApiData {
  const now = new Date();
  return {
    workflow: {
      id: workflowId,
      orgId,
      ownerId: userId,
      name: DEFAULT_NEW_WORKFLOW_NAME,
      status: 'active',
      createdAt: now,
      updatedAt: now,
    },
    meta: null,
  };
}

export function shouldSyncWorkflowDraftFromServer(params: {
  isHydratedFromServer: boolean;
  hasUnsavedChanges: boolean;
}): boolean {
  return (
    params.isHydratedFromServer === true && params.hasUnsavedChanges === false
  );
}

/**
 * Self-heal cadence for the workflow graph. `ResourceUpdated` broadcasts are the
 * primary live-update path, but they only land after the watchable subscribes —
 * so when Coco creates a workflow and immediately builds its graph, the panel can
 * mount mid-build and miss those early events. This reconcile poll re-fetches the
 * graph on a cadence to catch up after any missed event. The watchable's
 * `equalityFn` suppresses re-renders when nothing actually changed.
 */
const WORKFLOW_GRAPH_RECONCILE_MS = 4_000;

/**
 * Skeleton model for workflow detail pages. Loads and syncs workflow resource
 * data via `ResourceUpdated` broadcasts and `resource.get`.
 */
/**
 * How many graph edits the editor remembers for undo/redo. Draft snapshots are
 * small (a graph of nodes/edges), so this is generous; it only keeps a long
 * editing session from growing the stacks without limit.
 */
const UNDO_HISTORY_LIMIT = 50;

export class WorkflowModel extends BaseModel implements TargetableModel {
  readonly resourceType = 'workflow' as const;
  private _isTargeted = true;
  /**
   * The copies from the last paste, waiting to be selected on the canvas.
   *
   * Lives here because the paste does — it runs in an action, where there is no
   * component to hold the ids and no way to hand them back to one. Read once,
   * by the graph sync that renders them (see {@link takePastedNodeIds}).
   *
   * View state, deliberately: it never reaches the draft, so it cannot make the
   * workflow dirty or survive a reload.
   */
  private _pastedNodeIds: readonly string[] | null = null;
  /** Tail of the save queue; see {@link persistDraft}. */
  private _persistQueue: Promise<unknown> = Promise.resolve();

  readonly workflowId: string;
  readonly isNewWatchable: ReadonlyWatchable<boolean>;
  readonly hasUnsavedChangesWatchable: ReadonlyWatchable<boolean>;
  readonly nameWatchable: ReadonlyWatchable<string>;
  readonly resourceWatchable: ReadonlyWatchable<WorkflowResourceApiData | null>;
  readonly loadStatusWatchable: ReadonlyWatchable<
    'loading' | 'ready' | 'not-found'
  >;
  readonly graphWatchable: ReadonlyWatchable<WorkflowGraphApiData | null>;
  readonly editorSelectionWatchable: ReadonlyWatchable<WorkflowEditorSelection>;
  readonly editorTabWatchable: ReadonlyWatchable<WorkflowEditorTab>;
  readonly toolbarStateWatchable: ReadonlyWatchable<WorkflowToolbarState>;
  /** Whether there is a graph edit to undo / redo (drives Cmd+Z and any button). */
  readonly canUndoWatchable: ReadonlyWatchable<boolean>;
  readonly canRedoWatchable: ReadonlyWatchable<boolean>;
  readonly renderCatalog: WorkflowRenderCatalog;

  private readonly workflowDataEventWatchable: ReadonlyEventWatchable<WorkflowResourceState>;
  private readonly _client: Pick<TenantedClient, 'resource'>;
  private readonly _folderId: string | undefined;
  private readonly _orgId: string;
  private readonly _userId: string;
  private readonly _isNewWatchable: Watchable<boolean>;
  private readonly _draftNameWatchable: Watchable<string | null>;
  private readonly _draftStateWatchable: Watchable<WorkflowDraftState>;
  /**
   * Undo / redo history of draft-state snapshots. Every user edit funnels through
   * {@link updateDraftState}; the pure {@link WorkflowUndoHistory} holds the
   * stacks and the restore rules (see that class). Cleared only on the FIRST
   * server hydration, never on rehydrate — see {@link _clearUndoHistory}.
   */
  private readonly _history = new WorkflowUndoHistory(UNDO_HISTORY_LIMIT);
  /**
   * The draft state before the first graph edit of the current gesture, held
   * until a microtask commits the gesture as one undo step. Null between gestures.
   */
  private _undoBatchBase: WorkflowDraftState | null = null;
  /**
   * The most recent agent-title map handed to {@link healLegacyBindingSources}.
   * Undo/redo restore a *snapshot's* graph, which may predate the title-alias
   * heal (that second pass only runs once the agent list loads). We re-heal the
   * restored graph with this map so a legacy title ref can never ride an undo
   * back into a save — see {@link _rehealRestoredGraph}. Empty until the agent
   * list is known, which makes the re-heal a safe no-op.
   */
  private _lastAgentTitleById: ReadonlyMap<string, string> = new Map();
  private readonly _canUndoWatchable: Watchable<boolean>;
  private readonly _canRedoWatchable: Watchable<boolean>;
  private readonly _baselineDraftWatchable: Watchable<WorkflowDraftState>;
  private readonly _baselineNameWatchable: Watchable<string>;
  private readonly _hydratedFromServerWatchable: Watchable<boolean>;
  private readonly _editorSelectionWatchable: Watchable<WorkflowEditorSelection>;
  private readonly _editorTabWatchable: Watchable<WorkflowEditorTab>;
  private readonly _editorTabUrl: UrlParamWatchable<WorkflowEditorTab>;
  private readonly _initialEditorTab: WorkflowEditorTab;
  /**
   * The `tab` query value at the last sync (`undefined` before the first).
   * Lets {@link syncEditorTabFromUrl} ignore searchParams changes that leave the
   * tab param untouched — e.g. the open run id being written into the URL — so an
   * unrelated param write can't collapse the runs panel.
   */
  private _lastTabParamRaw: string | null | undefined = undefined;
  private readonly _toolbarExpandedWatchable: Watchable<boolean>;
  private readonly _nodeModelRegistry: WorkflowNodeModelRegistry;
  private readonly _watchablesFactory: WorkflowModelProps['watchablesFactory'];
  private readonly _webhookTriggerEditModels = new Map<
    string,
    WorkflowWebhookTriggerEditModel
  >();
  private readonly _webhookTriggerInstallPatchWatchable: ReadonlyEventWatchable<
    BroadcastEventPayloadMap[typeof BroadcastEventType.ResourceUpdated] | null
  >;
  private readonly _dispose: () => void;

  constructor(props: WorkflowModelProps) {
    super(props.workflowId);

    const { workflowId, client } = props;
    this._client = client;
    this._folderId = props.folderId;
    this._orgId = props.orgId;
    this._userId = props.userId;
    this._isNewWatchable = Watchable.fromValue(props.isNew);
    this._draftNameWatchable = Watchable.fromValue<string | null>(null);
    this._draftStateWatchable = Watchable.fromValue(
      createWorkflowDraftState({
        workflowId,
        orgId: props.orgId,
        userId: props.userId,
      })
    );
    const emptyDraft = this._draftStateWatchable.snapshot();
    const draftContext = {
      workflowId,
      orgId: props.orgId,
      userId: props.userId,
    };
    // Seeded from a trigger card: pre-bind that existing trigger so a brand-new
    // workflow opens with it on the graph. Both draft and baseline carry it (so
    // the trigger isn't treated as an unsaved edit) — mirrors how a server graph
    // hydrates both. Only for new workflows; hydration overwrites otherwise.
    const boundDraft =
      props.isNew === true && props.seedTrigger !== undefined
        ? draftBindExistingTrigger(emptyDraft, draftContext, props.seedTrigger)
        : emptyDraft;
    // Seeded from "Add automation" on a dataset: a brand-new dataset trigger the
    // draft opens watching that dataset. Unlike the bound trigger above it is
    // added to the draft ONLY — the baseline is `boundDraft`, without it — so it
    // reads as an unsaved edit and the save path creates the trigger rather than
    // binding to an id that does not exist yet.
    const initialDraft =
      props.isNew === true && props.seedNewDatasetTrigger !== undefined
        ? draftAddDatasetTrigger(boundDraft, draftContext, {
            name: props.seedNewDatasetTrigger.name,
            datasetId: props.seedNewDatasetTrigger.datasetId,
            criterion: props.seedNewDatasetTrigger.criterion,
            trackFields: null,
            filter: null,
            processExistingRows: false,
            runMode: props.seedNewDatasetTrigger.runMode,
          }).state
        : boundDraft;
    if (initialDraft !== emptyDraft) {
      this._draftStateWatchable.set(initialDraft);
    }
    this._baselineDraftWatchable = Watchable.fromValue(
      cloneWorkflowDraftState(boundDraft)
    );
    this._baselineNameWatchable = Watchable.fromValue(
      DEFAULT_NEW_WORKFLOW_NAME
    );
    this._hydratedFromServerWatchable = Watchable.fromValue(false);
    this._editorSelectionWatchable = Watchable.fromValue(
      EMPTY_WORKFLOW_EDITOR_SELECTION
    );
    // Standalone default matches URL semantics (runs panel open). Artifacts can
    // override via initialEditorTab (e.g. graph-only canvas).
    const initialEditorTab = props.initialEditorTab ?? 'runs';
    this._initialEditorTab = initialEditorTab;
    const editorTabUrl = new UrlParamWatchable<WorkflowEditorTab>(
      EDITOR_TAB_QUERY_KEY,
      {
        default: 'runs',
        parse: parseWorkflowEditorTabFromUrl,
        serialize: serializeWorkflowEditorTabToUrl,
      },
      props.urlAdapter,
      initialEditorTab
    );
    this._editorTabUrl = editorTabUrl;
    this._editorTabWatchable = editorTabUrl.asWatchable();
    this._toolbarExpandedWatchable = Watchable.fromValue(true);
    this._canUndoWatchable = Watchable.fromValue(false);
    this._canRedoWatchable = Watchable.fromValue(false);
    this.canUndoWatchable = this._canUndoWatchable;
    this.canRedoWatchable = this._canRedoWatchable;
    this.renderCatalog = props.renderCatalog ?? DEFAULT_WORKFLOW_RENDER_CATALOG;
    this._watchablesFactory = props.watchablesFactory;
    this._nodeModelRegistry = new WorkflowNodeModelRegistry({
      catalog: this.renderCatalog,
      client,
      watchablesFactory: props.watchablesFactory,
      isPreviewEntity: (entityId) =>
        isDraftOnlyTriggerInWorkflow(
          this._draftStateWatchable.snapshot(),
          entityId,
          this._isNewWatchable.snapshot()
        ),
    });
    const initialResource = createInitialWorkflowResource(
      props.orgId,
      props.userId,
      workflowId
    );
    const initialState: WorkflowResourceState =
      props.isNew === true
        ? { resource: initialResource, status: 'ready' }
        : { resource: null, status: 'loading' };

    this.workflowId = workflowId;
    this.isNewWatchable = this._isNewWatchable;

    this.workflowDataEventWatchable =
      props.watchablesFactory.makeResourceUpdatedWatchable<
        WorkflowResourceState,
        BroadcastEventPayloadMap[typeof BroadcastEventType.ResourceUpdated]
      >({
        runOnMount: props.isNew !== true,
        ...(props.isNew !== true && {
          reconcile: { cadence: WORKFLOW_GRAPH_RECONCILE_MS },
        }),
        initialValue: initialState,
        filter: (payload) =>
          payload?.resourceType === 'workflow' &&
          payload?.resourceId === workflowId,
        equalityFn: (a, b) =>
          a.status === b.status &&
          JSON.stringify(a.resource) === JSON.stringify(b.resource),
        fn: async (payload, get) => {
          if (
            payload !== undefined &&
            payload.resourceType === 'workflow' &&
            payload.workflow !== undefined
          ) {
            const previous = get(this.workflowDataEventWatchable);
            return {
              resource: toWorkflowResourceApiData(
                {
                  ...toWorkflowApiData(payload.workflow),
                  graph: previous.resource?.workflow.graph,
                },
                previous.resource?.meta ?? null
              ),
              status: 'ready',
            };
          }

          if (get(this._isNewWatchable)) {
            return get(this.workflowDataEventWatchable);
          }

          const result = await client.resource.get({
            type: ResourceType.Workflow,
            id: workflowId,
          });
          if (result.isErr()) {
            const appErr =
              result.error.t === 'application' ? result.error.err : null;
            if (
              appErr?.errorType === 'NotFound' ||
              appErr?.errorType === 'Forbidden'
            ) {
              const previous = get(this.workflowDataEventWatchable);
              if (previous.resource !== null) {
                return { resource: previous.resource, status: 'ready' };
              }
              return { resource: null, status: 'not-found' };
            }
            return get(this.workflowDataEventWatchable);
          }
          if (result.value.type !== 'workflow') {
            return get(this.workflowDataEventWatchable);
          }
          const w = result.value.workflow;
          return {
            resource: toWorkflowResourceApiData(
              {
                id: w.id,
                orgId: w.orgId,
                ownerId: w.ownerId,
                name: w.name,
                status: w.status,
                workflowStatus: w.workflowStatus,
                workflowType: w.workflowType,
                idempotencyKey: w.idempotencyKey ?? null,
                graph: w.graph as WorkflowGraphApiData | undefined,
                createdAt: w.createdAt,
                updatedAt: w.updatedAt,
              },
              result.value.meta ?? null
            ),
            status: 'ready',
          };
        },
      });

    this.resourceWatchable = Watchable.from(
      (get) => get(this.workflowDataEventWatchable).resource
    );
    this.loadStatusWatchable = Watchable.from(
      (get) => get(this.workflowDataEventWatchable).status
    );
    this.graphWatchable = Watchable.from(
      (get) => get(this._draftStateWatchable).graph
    );
    this.editorSelectionWatchable = this._editorSelectionWatchable;
    this.editorTabWatchable = this._editorTabWatchable;
    this.toolbarStateWatchable = Watchable.from((get) => {
      const selection = get(this._editorSelectionWatchable);
      return {
        expanded: get(this._toolbarExpandedWatchable),
        connectFromNodeId: resolveConnectFromNodeId(selection),
      };
    });
    this.nameWatchable = Watchable.from((get) => {
      const draftName = get(this._draftNameWatchable);
      if (draftName !== null && draftName.trim() !== '') {
        return draftName;
      }
      const resource = get(this.workflowDataEventWatchable).resource;
      return resource?.workflow.name ?? initialResource.workflow.name;
    });
    this.hasUnsavedChangesWatchable = Watchable.from((get) => {
      const draft = get(this._draftStateWatchable);
      const name = get(this.nameWatchable);
      if (get(this._isNewWatchable)) {
        return isWorkflowDraftDirty(draft, name);
      }
      if (!get(this._hydratedFromServerWatchable)) {
        return false;
      }
      return isWorkflowDraftDirtyAgainstBaseline(
        draft,
        get(this._baselineDraftWatchable),
        name,
        get(this._baselineNameWatchable)
      );
    });

    const hydrateFromResource = (resource: WorkflowResourceApiData): void => {
      if (this._hydratedFromServerWatchable.snapshot()) {
        return;
      }
      const graph =
        resource.workflow.graph ??
        createEmptyWorkflowDraftGraph({
          workflowId,
          orgId: props.orgId,
          userId: props.userId,
        });
      const draft = createDraftStateFromGraph(
        {
          workflowId,
          orgId: props.orgId,
          userId: props.userId,
        },
        graph,
        { idempotencyKey: resource.workflow.idempotencyKey ?? null }
      );
      this._draftStateWatchable.set(draft);
      this._baselineDraftWatchable.set(cloneWorkflowDraftState(draft));
      this._baselineNameWatchable.set(resource.workflow.name);
      this._hydratedFromServerWatchable.set(true);
      this._clearUndoHistory();
    };

    this.workflowDataEventWatchable.subscribe((state) => {
      if (state.status !== 'ready' || state.resource === null || props.isNew) {
        return;
      }
      if (this._hydratedFromServerWatchable.snapshot() === false) {
        hydrateFromResource(state.resource);
        return;
      }
      if (
        shouldSyncWorkflowDraftFromServer({
          isHydratedFromServer: this._hydratedFromServerWatchable.snapshot(),
          hasUnsavedChanges: this.hasUnsavedChangesWatchable.snapshot(),
        })
      ) {
        this.rehydrateDraftFromServer();
      }
    });

    this._nodeModelRegistry.syncGraph(
      this._draftStateWatchable.snapshot().graph
    );
    this._draftStateWatchable.subscribe((draft) => {
      this._nodeModelRegistry.syncGraph(draft.graph);
    });

    this._webhookTriggerInstallPatchWatchable =
      props.watchablesFactory.makeResourceUpdatedWatchable({
        runOnMount: true,
        initialValue: null,
        filter: (payload) =>
          payload?.resourceType === 'workflow_trigger' &&
          payload.webhookInstall !== undefined &&
          this._draftStateWatchable
            .snapshot()
            .graph.triggers.some(
              (snapshot) =>
                snapshot.kind === PersistedWorkflowTriggerKind.Webhook &&
                snapshot.trigger.id === payload.resourceId
            ),
        fn: async (payload) => payload ?? null,
      });

    this._webhookTriggerInstallPatchWatchable.subscribe((payload) => {
      if (
        payload === null ||
        payload.resourceType !== 'workflow_trigger' ||
        payload.webhookInstall === undefined
      ) {
        return;
      }
      // Install state is the server's, not an edit, so the baseline takes it too.
      // Patched into the draft alone it read as an unsaved change, and every open
      // editor autosaved its whole graph at once whenever a webhook installed.
      const patched = draftPatchWebhookTriggerInstallState(
        this._draftStateWatchable.snapshot(),
        payload.resourceId,
        payload.webhookInstall
      );
      const patchedBaseline = draftPatchWebhookTriggerInstallState(
        this._baselineDraftWatchable.snapshot(),
        payload.resourceId,
        payload.webhookInstall
      );
      if (patchedBaseline !== null) {
        this._baselineDraftWatchable.set(patchedBaseline);
      }
      if (patched !== null) {
        this._draftStateWatchable.set(patched);
      }
    });

    this._dispose = () => {
      this.workflowDataEventWatchable.unsubscribe();
      this._webhookTriggerInstallPatchWatchable.unsubscribe();
      for (const model of this._webhookTriggerEditModels.values()) {
        model.dispose();
      }
      this._webhookTriggerEditModels.clear();
      this._nodeModelRegistry.dispose();
    };
  }

  getWebhookTriggerEditModel(params: {
    triggerId: string;
    isDraftOnly: boolean;
  }): WorkflowWebhookTriggerEditModel {
    const key = `${params.triggerId}:${
      params.isDraftOnly ? 'draft' : 'persisted'
    }`;
    for (const [existingKey, model] of this._webhookTriggerEditModels) {
      if (
        existingKey.startsWith(`${params.triggerId}:`) &&
        existingKey !== key
      ) {
        model.dispose();
        this._webhookTriggerEditModels.delete(existingKey);
      }
    }
    const existing = this._webhookTriggerEditModels.get(key);
    if (existing !== undefined) {
      return existing;
    }
    const model = new WorkflowWebhookTriggerEditModel({
      triggerId: params.triggerId,
      isDraftOnly: params.isDraftOnly,
      graphWatchable: this.graphWatchable,
      client: this._client,
      watchablesFactory: this._watchablesFactory,
    });
    this._webhookTriggerEditModels.set(key, model);
    return model;
  }

  getRenderableNodeModel<TModel extends WorkflowDisposable>(
    descriptorKey: string,
    entityId: string
  ): TModel {
    return this._nodeModelRegistry.getModel(descriptorKey, entityId);
  }

  get isTargeted(): boolean {
    return this._isTargeted;
  }

  target(): void {
    this._isTargeted = true;
  }

  markAsNotTargeted(): void {
    this._isTargeted = false;
  }

  get isNew(): boolean {
    return this._isNewWatchable.snapshot();
  }

  markNoLongerNew(): void {
    this._isNewWatchable.set(false);
  }

  markAsSaved(): void {
    const draft = this._draftStateWatchable.snapshot();
    this._baselineDraftWatchable.set(cloneWorkflowDraftState(draft));
    this._baselineNameWatchable.set(this.getNameForSave());
    this._draftNameWatchable.set(null);
    this.markNoLongerNew();
  }

  /**
   * Re-runs the legacy binding-source heal now that agent titles are known.
   * Hydration can only match names the graph itself derives (`Agent 3946c56f…`);
   * a ref that used an agent's title needs the loaded agent list.
   *
   * Draft and baseline are healed together so a repair never registers as an
   * unsaved change — the graph the user next saves is simply the correct one.
   */
  healLegacyBindingSources(agentTitleById: ReadonlyMap<string, string>): void {
    // Remember the freshest map so undo/redo can re-heal a restored snapshot
    // even between calls from the hook.
    this._lastAgentTitleById = agentTitleById;
    const draft = this._draftStateWatchable.snapshot();
    const aliases = agentTitleAliasesForGraph(draft.graph, agentTitleById);
    if (aliases.size === 0) {
      return;
    }
    const healedGraph = healWorkflowGraphBindingSources(draft.graph, aliases);
    if (healedGraph === draft.graph) {
      return;
    }
    this._draftStateWatchable.set({ ...draft, graph: healedGraph });
    const baseline = this._baselineDraftWatchable.snapshot();
    this._baselineDraftWatchable.set({
      ...baseline,
      graph: healWorkflowGraphBindingSources(baseline.graph, aliases),
    });
  }

  getBaselineDraftState(): WorkflowDraftState {
    return this._baselineDraftWatchable.snapshot();
  }

  getDraftState(): WorkflowDraftState {
    return this._draftStateWatchable.snapshot();
  }

  updateDraftState(
    updater: (state: WorkflowDraftState) => WorkflowDraftState
  ): void {
    const prev = this._draftStateWatchable.snapshot();
    const next = updater(prev);
    // Draft mutations return the same reference for a no-op (e.g. an invalid
    // for-each adoption). Recording it would add an undo step that changes
    // nothing, so skip both the history push and the set.
    if (next === prev) {
      return;
    }
    // Coalesce every graph edit in the same synchronous gesture into ONE undo
    // step. A single user action can fan into several updateDraftState calls —
    // deleting a node emits the node removal and each connected edge removal as
    // separate calls, across React Flow's onNodesChange and onEdgesChange — and
    // the user expects one Cmd+Z to bring the whole thing back. We remember the
    // state before the first graph edit and commit it to history on a microtask,
    // once the gesture's synchronous calls have all run. Non-graph edits
    // (idempotency key setting, webhook-install patch) never open a batch.
    if (next.graph !== prev.graph) {
      this._openUndoBatch(prev);
    }
    this._draftStateWatchable.set(next);
  }

  /**
   * Begin (or extend) the current coalesced undo batch. The first graph edit of a
   * gesture records the pre-gesture state and schedules the commit; later edits in
   * the same tick fold into it, so the whole gesture is one undo step.
   */
  private _openUndoBatch(base: WorkflowDraftState): void {
    if (this._undoBatchBase !== null) {
      return;
    }
    this._undoBatchBase = base;
    queueMicrotask(() => this._commitUndoBatch());
  }

  /** Flush the open batch into history (idempotent — safe to call eagerly). */
  private _commitUndoBatch(): void {
    const base = this._undoBatchBase;
    if (base === null) {
      return;
    }
    this._undoBatchBase = null;
    if (this._history.record(base, this._draftStateWatchable.snapshot())) {
      this._syncUndoWatchables();
    }
  }

  /** Revert the last graph edit. No-op when there is nothing to undo. */
  undo(): void {
    // Flush a batch still open from a gesture in this same tick, so the edit the
    // user is undoing is already on the stack.
    this._commitUndoBatch();
    const applied = this._history.undo(this._draftStateWatchable.snapshot());
    if (applied === null) {
      return;
    }
    this._draftStateWatchable.set(applied);
    this._rehealRestoredGraph();
    this._syncUndoWatchables();
  }

  /** Re-apply the last undone graph edit. No-op when there is nothing to redo. */
  redo(): void {
    this._commitUndoBatch();
    const applied = this._history.redo(this._draftStateWatchable.snapshot());
    if (applied === null) {
      return;
    }
    this._draftStateWatchable.set(applied);
    this._rehealRestoredGraph();
    this._syncUndoWatchables();
  }

  /**
   * Re-run the legacy binding-source heal on a just-restored snapshot.
   *
   * A snapshot can predate the title-alias heal — it is captured in
   * {@link updateDraftState} the moment the user edits, which can be before the
   * agent list loads and {@link healLegacyBindingSources} runs its second pass.
   * Restoring that graph and letting it autosave would persist a title ref the
   * save endpoint rejects (it only accepts ids). Healing here closes that window
   * at the model layer rather than leaning on the heal hook's effect firing in
   * time. The heal is idempotent and patches draft + baseline together, so a
   * healthy graph is a no-op and the undone change stays dirty (and thus saved).
   */
  private _rehealRestoredGraph(): void {
    if (this._lastAgentTitleById.size === 0) {
      return;
    }
    this.healLegacyBindingSources(this._lastAgentTitleById);
  }

  /**
   * Drop all undo/redo history. Called once, on the FIRST hydration from the
   * server: that is the starting point, with nothing before it to undo.
   *
   * Deliberately NOT called on {@link rehydrateDraftFromServer}. Rehydrate runs
   * after every autosave (to pick up the server's normalized graph + idempotency
   * key) and only when there are no unsaved local edits; clearing there wiped the
   * user's undo stack a couple of seconds after every edit — "undo only works
   * before autosave". The autosave echo is the user's own graph, so the stack
   * stays valid across it. (The remaining edge is a *different* editor saving the
   * same workflow while this one is idle: undo could then revert onto the stale
   * local graph. Rare for single-owner workflow editing, and preferable to
   * breaking undo on every autosave; revisit if concurrent editing lands.)
   */
  private _clearUndoHistory(): void {
    // Drop any gesture still waiting to commit, so it can't land post-clear.
    this._undoBatchBase = null;
    if (this._history.clear()) {
      this._syncUndoWatchables();
    }
  }

  private _syncUndoWatchables(): void {
    this._canUndoWatchable.set(this._history.canUndo);
    this._canRedoWatchable.set(this._history.canRedo);
  }

  /** Patch baseline in place (e.g. after an immediate trigger API save). */
  updateBaselineDraftState(
    updater: (state: WorkflowDraftState) => WorkflowDraftState
  ): void {
    this._baselineDraftWatchable.set(
      updater(this._baselineDraftWatchable.snapshot())
    );
  }

  getDraftContext(): {
    workflowId: string;
    orgId: string;
    userId: string;
  } {
    return {
      workflowId: this.workflowId,
      orgId: this._orgId,
      userId: this._userId,
    };
  }

  get workflowClient(): Pick<
    TenantedClient,
    'resource'
  >['resource']['workflow'] {
    return this._client.resource.workflow;
  }

  get folderId(): string | undefined {
    return this._folderId;
  }

  getIdempotencyKey(): PersistedWorkflow.WorkflowIdempotencyKey | null {
    return this._draftStateWatchable.snapshot().idempotencyKey;
  }

  setIdempotencyKey(
    idempotencyKey: PersistedWorkflow.WorkflowIdempotencyKey | null
  ): void {
    this.updateDraftState((state) => ({ ...state, idempotencyKey }));
  }

  async persistIdempotencyKey(
    idempotencyKey: PersistedWorkflow.WorkflowIdempotencyKey | null
  ): Promise<{ success: true } | { success: false; message: string }> {
    this.setIdempotencyKey(idempotencyKey);
    if (this.isNew) {
      return { success: true };
    }
    const updateResult = await this._client.resource.workflow.update({
      id: this.workflowId,
      idempotencyKey,
    });
    if (updateResult.isErr()) {
      const message =
        updateResult.error.t === 'application'
          ? updateResult.error.err.message ?? 'Failed to save idempotency key'
          : 'Failed to save idempotency key';
      return { success: false, message };
    }
    this._baselineDraftWatchable.set({
      ...cloneWorkflowDraftState(this._baselineDraftWatchable.snapshot()),
      idempotencyKey,
    });
    return { success: true };
  }

  hasDraftChangesAgainstBaseline(): boolean {
    return isWorkflowDraftDirtyAgainstBaseline(
      this.getDraftState(),
      this.getBaselineDraftState(),
      this.getNameForSave(),
      this._baselineNameWatchable.snapshot()
    );
  }

  getNameForSave(): string {
    return this.nameWatchable.snapshot();
  }

  setNameForSave(name: string): void {
    this._draftNameWatchable.set(name.trim());
  }

  refreshGraph(): void {
    void this.workflowDataEventWatchable.refresh();
  }

  setEditorSelection(selection: WorkflowEditorSelection): void {
    const current = this._editorSelectionWatchable.snapshot();
    if (
      current.selectedNodeIds.length === selection.selectedNodeIds.length &&
      current.selectedEdgeIds.length === selection.selectedEdgeIds.length &&
      current.selectedNodeIds.every(
        (nodeId, index) => nodeId === selection.selectedNodeIds[index]
      ) &&
      current.selectedEdgeIds.every(
        (edgeId, index) => edgeId === selection.selectedEdgeIds[index]
      )
    ) {
      return;
    }
    this._editorSelectionWatchable.set(selection);
  }

  /**
   * Adds a copied slice of a graph to the draft as new nodes, wired in per
   * `anchor`, and marks the copies as just-pasted.
   *
   * Synchronous, unlike the other draft mutations: the caller needs to know
   * whether anything landed and whether it attached, so it can say so.
   */
  pasteNodes(
    payload: WorkflowClipboardPayload,
    anchor?: WorkflowPasteAnchor
  ): { nodeIds: readonly string[]; attached: boolean } {
    const result = draftPasteWorkflowNodes(
      this.getDraftState(),
      this.getDraftContext(),
      payload,
      anchor
    );
    if (result.nodeIds.length === 0) {
      return { nodeIds: [], attached: false };
    }
    this.updateDraftState(() => result.state);
    this._pastedNodeIds = result.nodeIds;
    return { nodeIds: result.nodeIds, attached: result.attached };
  }

  /**
   * The copies from the last paste, once — null if there is nothing waiting.
   *
   * Spent on read, because the graph sync that carries the paste is the only
   * one that can select them: if the copies aren't in it they never will be,
   * and a later sync forcing the selection back onto them would take it from
   * whatever the user has since clicked.
   */
  takePastedNodeIds(): readonly string[] | null {
    const nodeIds = this._pastedNodeIds;
    this._pastedNodeIds = null;
    return nodeIds;
  }

  setToolbarExpanded(expanded: boolean): void {
    if (this._toolbarExpandedWatchable.snapshot() === expanded) {
      return;
    }
    this._toolbarExpandedWatchable.set(expanded);
  }

  toggleToolbarExpanded(): void {
    this.setToolbarExpanded(!this._toolbarExpandedWatchable.snapshot());
  }

  setEditorTab(tab: WorkflowEditorTab): void {
    if (this._editorTabWatchable.snapshot() === tab) {
      return;
    }
    this._editorTabUrl.set(tab);
  }

  /** Sync runs-panel open state from the chat URL (e.g. browser back/forward). */
  syncEditorTabFromUrl(searchParams: URLSearchParams): void {
    // Delegated to a pure resolver so the "unrelated param write must not flip the
    // tab" rule (which keeps a written `?run=` from collapsing the runs panel) is
    // unit-testable without constructing a whole model. See
    // resolveWorkflowEditorTabSync.
    const { tab, lastRaw } = resolveWorkflowEditorTabSync({
      raw: searchParams.get(EDITOR_TAB_QUERY_KEY),
      lastRaw: this._lastTabParamRaw,
      initialTab: this._initialEditorTab,
    });
    this._lastTabParamRaw = lastRaw;
    if (tab !== null) {
      this._editorTabWatchable.setFromSource(tab);
    }
  }

  rehydrateDraftFromServer(): void {
    const resource = this.resourceWatchable.snapshot();
    if (resource?.workflow.graph === undefined) {
      return;
    }
    const currentDraft = this._draftStateWatchable.snapshot();
    const nextIdempotencyKey = resource.workflow.idempotencyKey ?? null;
    if (
      JSON.stringify(currentDraft.graph) ===
        JSON.stringify(resource.workflow.graph) &&
      this._baselineNameWatchable.snapshot() === resource.workflow.name &&
      currentDraft.idempotencyKey === nextIdempotencyKey
    ) {
      return;
    }
    const draft = createDraftStateFromGraph(
      this.getDraftContext(),
      resource.workflow.graph,
      { idempotencyKey: nextIdempotencyKey }
    );
    this._draftStateWatchable.set(draft);
    this._baselineDraftWatchable.set(cloneWorkflowDraftState(draft));
    this._baselineNameWatchable.set(resource.workflow.name);
    this._hydratedFromServerWatchable.set(true);
    // NB: intentionally does not clear undo history — see _clearUndoHistory.
    // Rehydrate is the autosave echo; wiping here broke undo after every save.
  }

  /**
   * Brings the editor in line with what a save just wrote.
   *
   * Edits made while the request was in flight are kept: the saved graph becomes
   * the baseline, so they read as unsaved and the next autosave sends them.
   * Replacing the draft with the saved graph, as this used to, silently dropped
   * them.
   */
  private async settleAfterSave(submitted: WorkflowDraftState): Promise<void> {
    await this.workflowDataEventWatchable.refresh();
    if (this.getDraftState() === submitted) {
      this.rehydrateDraftFromServer();
      this.markAsSaved();
      return;
    }
    const resource = this.resourceWatchable.snapshot();
    if (resource?.workflow.graph !== undefined) {
      this._baselineDraftWatchable.set(
        createDraftStateFromGraph(
          this.getDraftContext(),
          resource.workflow.graph,
          { idempotencyKey: resource.workflow.idempotencyKey ?? null }
        )
      );
      this._baselineNameWatchable.set(resource.workflow.name);
    }
    this.markNoLongerNew();
  }

  /**
   * Saves the draft. Saves run one at a time, in the order they were asked for.
   *
   * A save replaces the workflow's whole graph, and autosave, Ctrl+S, the
   * navigation save and Save Artifacts can each fire within moments of another.
   * Queued, each save sends the draft as it stands when its turn comes, rather
   * than two racing to replace the graph at once.
   */
  persistDraft(params?: {
    name?: string;
    folderId?: string;
  }): Promise<{ success: true } | { success: false; message: string }> {
    const result = this._persistQueue.then(() => this.persistDraftNow(params));
    this._persistQueue = result.catch(() => undefined);
    return result;
  }

  private async persistDraftNow(params?: {
    name?: string;
    folderId?: string;
  }): Promise<{ success: true } | { success: false; message: string }> {
    const resolvedName = (params?.name ?? this.getNameForSave()).trim();
    if (resolvedName === '') {
      return { success: false, message: 'Workflow name is required' };
    }

    this.setNameForSave(resolvedName);
    const folderId = params?.folderId ?? this._folderId;

    if (this.isNew) {
      const created = this.getDraftState();
      const createResult = await this._client.resource.workflow.create({
        id: this.workflowId,
        name: resolvedName,
        ...(folderId !== undefined && { folderId }),
        // Pass the baseline so a trigger the workflow was *seeded* with (an
        // existing trigger, present in both draft + baseline) serializes as a
        // bind (`kind: 'existing'`) rather than a duplicate cron create.
        graph: draftStateToGraphCreateInput(
          created,
          this.getBaselineDraftState()
        ),
        idempotencyKey: this.getIdempotencyKey(),
      });
      if (createResult.isErr()) {
        if (!isWorkflowAlreadyExistsError(createResult.error)) {
          return {
            success: false,
            message: workflowSaveErrorMessage(
              createResult.error,
              'Failed to create workflow'
            ),
          };
        }
        // Created in a prior request (or another tab) while this client still
        // thought the workflow was new — fall through to update.
        this.markNoLongerNew();
      } else {
        this.markNoLongerNew();
        await this.settleAfterSave(created);
        return { success: true };
      }
    }

    const updated = this.getDraftState();
    const updateResult = await this._client.resource.workflow.update({
      id: this.workflowId,
      name: resolvedName,
      graph: draftStateToGraphCreateInput(
        updated,
        this.getBaselineDraftState()
      ),
      idempotencyKey: this.getIdempotencyKey(),
    });
    if (updateResult.isErr()) {
      return {
        success: false,
        message: workflowSaveErrorMessage(
          updateResult.error,
          'Failed to save workflow'
        ),
      };
    }

    await this.settleAfterSave(updated);
    return { success: true };
  }

  /**
   * Starts a run from the current unsaved draft by persisting an ephemeral
   * workflow snapshot on the server. Does not mark the editor draft as saved.
   */
  async startDraftTestRun(params?: { runInput?: unknown }): Promise<
    | {
        success: true;
        runId: string;
        workflowId: string;
        ephemeralWorkflowId: string;
      }
    | { success: false; message: string }
  > {
    const graph = draftStateToGraphCreateInput(
      this.getDraftState(),
      this.isNew ? undefined : this.getBaselineDraftState()
    );
    const result = await this._client.resource.workflow.draftTestRun({
      workflowId: this.workflowId,
      graph: {
        nodes: graph.nodes,
        edges: graph.edges,
      },
      name: this.getNameForSave(),
      ...(params?.runInput !== undefined && { runInput: params.runInput }),
    });
    if (result.isErr()) {
      const message =
        result.error.t === 'application'
          ? result.error.err.message ?? 'Failed to start draft test run'
          : 'Failed to start draft test run';
      return { success: false, message };
    }
    return {
      success: true,
      runId: result.value.runId,
      workflowId: result.value.workflowId,
      ephemeralWorkflowId: result.value.ephemeralWorkflowId,
    };
  }

  async triggerWorkflow(params?: {
    runInput?: unknown;
  }): Promise<
    | { success: true; runId: string; workflowId: string }
    | { success: false; message: string }
  > {
    if (this.isNew === true) {
      return {
        success: false,
        message: 'Save the workflow before running it',
      };
    }

    const result = await this._client.resource.workflow.trigger({
      workflowId: this.workflowId,
      runInput: params?.runInput ?? undefined,
    });
    if (result.isErr()) {
      const message =
        result.error.t === 'application'
          ? result.error.err.message ?? 'Failed to trigger workflow'
          : 'Failed to trigger workflow';
      return { success: false, message };
    }
    return {
      success: true,
      runId: result.value.runId,
      workflowId: result.value.workflowId,
    };
  }

  dispose(): void {
    this._dispose();
  }

  developerDetails(): DeveloperDetails {
    const resource = this.resourceWatchable.snapshot();
    const workflow = resource?.workflow;
    const entries = [
      { key: 'Workflow ID', value: this.workflowId },
      { key: 'Org ID', value: this._orgId },
      { key: 'User ID', value: this._userId },
    ];

    if (workflow?.ownerId !== undefined) {
      entries.push({ key: 'Owner ID', value: workflow.ownerId });
    }
    if (workflow?.status !== undefined) {
      entries.push({ key: 'Status', value: workflow.status });
    }
    if (workflow?.workflowStatus !== undefined) {
      entries.push({ key: 'Workflow status', value: workflow.workflowStatus });
    }
    if (workflow?.workflowType !== undefined) {
      entries.push({ key: 'Workflow type', value: workflow.workflowType });
    }

    const idempotencyKey = this.getIdempotencyKey();
    if (idempotencyKey !== null) {
      entries.push({
        key: 'Idempotency key',
        value: JSON.stringify(idempotencyKey),
      });
    }
    if (this._folderId !== undefined) {
      entries.push({ key: 'Folder ID', value: this._folderId });
    }

    return {
      title: 'Workflow',
      entries,
    };
  }
}

const workflowModelContext = createProvidedModelContext(
  WorkflowModel,
  'WorkflowModel not found in scope'
);

/**
 * Provides the workflow model instance for the current editor tree so
 * `useWorkflowModel` binds correctly when several workflows share one actions
 * scope. Pass `target` for the active artifact tab only.
 */
export const WorkflowModelProvider = workflowModelContext.Provider;

export const useWorkflowModel = workflowModelContext.useModel;
/** Null outside a `WorkflowModelProvider` (e.g. run-detail surfaces). */
export const useWorkflowModelOrNull = workflowModelContext.useModelOrNull;

/**
 * The server's own reason a save was refused, whichever error class it arrived
 * as. The workflow controller throws bare Nest exceptions
 * (`{ errorType, message }`) rather than the `{ t: 'application', err }`
 * envelope, so its ValidationError / NotFound messages reach the client as
 * `t: 'server'`. Gating on `t === 'application'` swapped every one of them for
 * the generic fallback, which left a failed save with no stated reason. The
 * message is already normalized off all three error classes by `parseApiErr`,
 * so read it directly and keep the fallback for genuinely bodiless failures.
 */
export function workflowSaveErrorMessage(
  error: ApiErr,
  fallback: string
): string {
  const message = error.err.message;
  return message !== undefined && message.trim() !== '' ? message : fallback;
}

function isWorkflowAlreadyExistsError(error: ApiErr): boolean {
  const message = error.err.message ?? '';
  return message.includes('Workflow already exists');
}
